import { loadStripe, Stripe } from "@stripe/stripe-js";
import { capturePaymentError } from "./sentry";
import { safeGetItem, safeRemoveItem, safeSetItem } from "@mediatechgroup/app-version";

/**
 * Shared Stripe promise instance.
 * Uses lazy initialization to avoid loading Stripe multiple times.
 * This fixes the "Stripe() was called many times" warning.
 */
let stripePromiseInstance: Promise<Stripe | null> | null = null;
let currentStripeKey: string | null = null;
let stripeLoadError: string | null = null;

// In-memory fallback for the Stripe public key. Embed iframes
// (the `/embed/...` routes) frequently run in third-party storage
// contexts where `localStorage.setItem` silently no-ops — every
// subsequent `getItem` then returns null even though
// initializeTenantSupabase "successfully" wrote the key. The
// 10s wait below can't recover that, and the breadcrumb
// `stripeKeyTimeout` fires 100% of the time. Sentry issue
// CLUB-CENTRAL-TENANT-3N.
//
// supabaseClient.initializeTenantSupabase calls
// `setStripePublicKey()` after the central-DB tenant fetch so the
// key is always available in-memory regardless of localStorage's
// state. localStorage is still attempted for cross-tab persistence.
let inMemoryStripePublicKey: string | null = null;

/**
 * The club's configuration has loaded and it has NO publishable key: this
 * club does not take card payments. Known as soon as tenant init finishes
 * (setStripePublicKey(null)), so screens can say so straight away instead of
 * waiting 12 seconds and then telling the family to refresh the page
 * (2026-10-10: Payment Settings at a club without card processing said
 * "Payment system is taking longer than expected to load").
 */
let cardProcessingNotSetUp = false;

/** What a family or staff member is told when the club does not take cards. */
export const CARD_PAYMENTS_NOT_SET_UP = "Card payments are not set up for this club.";

/** True once the club's configuration has loaded without a card-processing key. */
export const isCardProcessingNotSetUp = (): boolean => cardProcessingNotSetUp;

export const setStripePublicKey = (key: string | null): void => {
  inMemoryStripePublicKey = key && key.trim() !== "" ? key : null;
  // Only called once the club's configuration is in hand (supabaseClient
  // initializeTenantSupabase), so "no key" here is the club's answer.
  cardProcessingNotSetUp = inMemoryStripePublicKey === null;
  try {
    if (inMemoryStripePublicKey) {
      safeSetItem("stripePublicKey", inMemoryStripePublicKey);
    } else {
      safeRemoveItem("stripePublicKey");
    }
  } catch {
    // localStorage blocked (iframe / privacy mode) — in-memory is
    // sufficient for the lifetime of this page.
  }
};

const readStripePublicKey = (): string | null => {
  let fromStorage: string | null = null;
  try {
    fromStorage = safeGetItem("stripePublicKey");
  } catch {
    // localStorage read blocked — fall through to in-memory.
  }
  if (fromStorage && fromStorage.trim() !== "") return fromStorage;
  if (inMemoryStripePublicKey && inMemoryStripePublicKey.trim() !== "") {
    return inMemoryStripePublicKey;
  }
  return null;
};

/**
 * Returns a user-facing error message if Stripe.js failed to load
 * (e.g. blocked by an ad blocker or content blocker), or null if OK.
 */
export const getStripeLoadError = (): string | null => stripeLoadError;

/**
 * Get the shared Stripe instance.
 * This function ensures Stripe is only initialized once per key.
 * If the key in localStorage changes, it will reinitialize.
 *
 * Race protection: previously this returned `Promise.resolve(null)`
 * synchronously when localStorage had no `stripePublicKey`, which
 * was the typical state during the brief window between page load
 * and the central-DB tenant-config fetch completing. Elements mounts
 * once with the resolved-null promise and is then PERMANENTLY bound
 * to a null Stripe instance — every subsequent click silently no-ops
 * (`useStripe()` returns null → `if (!stripe) return;`). Members
 * arriving cold from a marketing link hit this constantly; staff
 * (already deep in the app with localStorage populated) don't.
 *
 * Now: if the key isn't in localStorage yet, await tenant client
 * readiness (up to 10s) before reading again. The returned promise
 * stays unresolved until the key arrives, so Elements waits — and
 * mounts with a real Stripe instance — instead of locking in null.
 */
// Poll for stripePublicKey to appear in localStorage OR the
// in-memory fallback (set by setStripePublicKey from
// initializeTenantSupabase). Embed iframes often have localStorage
// blocked, so we MUST also check the in-memory variable —
// otherwise the wait never resolves even when the central-DB
// fetch completes successfully. Defined inline (rather than
// importing waitForTenantClient from supabaseClient) to keep
// stripeClient free of a supabaseClient circular import —
// supabaseClient already imports resetStripeClient from this file.
const waitForStripeKey = (timeoutMs: number): Promise<string | null> =>
  new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const k = readStripePublicKey();
      if (k) return resolve(k);
      // The club's configuration arrived with no key: nothing to wait for.
      if (cardProcessingNotSetUp) return resolve(null);
      if (Date.now() - start >= timeoutMs) return resolve(null);
      setTimeout(tick, 100);
    };
    tick();
  });

export const getStripePromise = async (
  options?: { keyWaitTimeoutMs?: number }
): Promise<Stripe | null> => {
  let localStripePublicKey = readStripePublicKey();

  if (!localStripePublicKey) {
    // Race window: initializeTenantSupabase() hasn't written the key
    // yet. Worst-case central-DB init is ~11s (5s timeout × 2 attempts
    // + 1s delay between them — see supabaseClient.ts:99-125). We
    // previously waited 10s here, which lost the race against the
    // longest-tail init runs — Sentry CLUB-CENTRAL-TENANT-3N. Now wait
    // 12s for a 1s slack budget. Returning a still-pending promise
    // means Elements waits to mount — instead of mounting with
    // stripe=null and locking every subsequent click into a silent
    // no-op.
    const timeoutMs = options?.keyWaitTimeoutMs ?? 12000;
    localStripePublicKey = await waitForStripeKey(timeoutMs);

    if (!localStripePublicKey && cardProcessingNotSetUp) {
      // The club has no card processing. That is an answer, not a fault:
      // no Sentry report, and no advice to refresh.
      stripeLoadError = CARD_PAYMENTS_NOT_SET_UP;
      return null;
    }

    if (!localStripePublicKey) {
      // Tenant init genuinely timed out. That warrants a Sentry
      // breadcrumb; without it the failure is invisible in production.
      stripeLoadError =
        "Payment system is taking longer than expected to load. Please refresh the page.";
      capturePaymentError(
        new Error(`getStripePromise: stripePublicKey not available after ${timeoutMs}ms (localStorage + in-memory fallback both empty)`),
        { operation: "stripeKeyTimeout" } as any
      );
      return null;
    }
  }

  if (localStripePublicKey !== currentStripeKey) {
    stripePromiseInstance = null;
    currentStripeKey = null;
  }

  if (!stripePromiseInstance) {
    currentStripeKey = localStripePublicKey;
    stripeLoadError = null;
    stripePromiseInstance = loadStripe(localStripePublicKey).catch((err) => {
      stripeLoadError =
        'Payment processing is unavailable. Please disable any ad blockers or content blockers for this site, then reload the page.';
      capturePaymentError(err, { operation: 'loadStripe', blocked: true } as any);
      return null;
    });
  }

  return stripePromiseInstance;
};

/**
 * Reset the Stripe client instance.
 * Call this when switching tenants to ensure fresh initialization.
 */
export const resetStripeClient = (): void => {
  stripePromiseInstance = null;
  currentStripeKey = null;
  stripeLoadError = null;
  // A different club (or a fresh load of this one) answers again.
  cardProcessingNotSetUp = false;
};

/**
 * @deprecated Use getStripePromise() instead.
 * This was previously evaluated at module load time, eagerly downloading Stripe.js
 * and blocking FID. Now returns null — components must call getStripePromise() lazily.
 */
export const stripePromise = null;

// Function to create a payment intent
export const createPaymentIntent = async (invoiceId: string, token: string) => {
  try {
    const response = await fetch(`/api/invoices/${invoiceId}/payment-intent`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to create payment intent");
    }

    return await response.json();
  } catch (error) {
    capturePaymentError(error, { operation: 'createPaymentIntent' });
    throw error;
  }
};

// Function to handle payment completion
export const handlePaymentCompletion = async (
  paymentIntentId: string,
  token: string
) => {
  try {
    const response = await fetch(`/api/payments/confirm`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ paymentIntentId }),
    });

    if (!response.ok) {
      throw new Error("Failed to confirm payment");
    }

    return await response.json();
  } catch (error) {
    capturePaymentError(error, { operation: 'confirmPayment' });
    throw error;
  }
};

export default getStripePromise;
